import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/layout/Header';
import PaymentModal from '../components/payment/PaymentModal';
import { useAuth } from '../contexts/AuthContext';

const PricingPage: React.FC = () => {
  const { isAuthenticated } = useAuth();
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<{
    id: string;
    name: string;
    price: number;
  } | null>(null);

  const plans = [
    {
      id: 'free',
      name: '免费版',
      price: 0,
      period: '月',
      description: '适合个人用户尝试',
      features: [
        '每月3个AI剧本生成',
        '基础角色管理（最多5个角色）',
        '基础分镜设计',
        '浏览资源中心',
        '最多3个项目',
        '社区支持'
      ],
      cta: '开始使用',
      popular: false
    },
    {
      id: 'pro',
      name: '专业版',
      price: 29,
      period: '月',
      description: '适合专业创作者',
      features: [
        '无限AI剧本生成',
        '无限角色管理',
        '高级分镜设计功能',
        'AI角色生成',
        '无限项目数量',
        '多种格式导出',
        '优先技术支持',
        '高级模板库'
      ],
      cta: '立即升级',
      popular: true
    },
    {
      id: 'enterprise',
      name: '企业版',
      price: 99,
      period: '月',
      description: '适合团队和企业',
      features: [
        '专业版所有功能',
        '团队协作功能',
        'API接口访问',
        '定制化模板',
        '数据分析报告',
        '专属客户经理',
        '企业级安全',
        '定制化培训'
      ],
      cta: '联系销售',
      popular: false
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 to-purple-800 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl font-bold mb-4">选择适合您的方案</h1>
          <p className="text-xl text-purple-100 max-w-2xl mx-auto">
            从个人创作到企业协作，我们为您提供最适合的AI短剧创作解决方案
          </p>
        </div>
      </div>

      {/* Pricing Cards */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {plans.map((plan) => (
            <div
              key={plan.id}
              className={`relative bg-white rounded-2xl shadow-lg border-2 ${
                plan.popular ? 'border-purple-600 scale-105' : 'border-gray-200'
              } hover:shadow-xl transition-all duration-300`}
            >
              {plan.popular && (
                <div className="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
                  <span className="bg-purple-600 text-white px-4 py-1 rounded-full text-sm font-medium">
                    最受欢迎
                  </span>
                </div>
              )}

              <div className="p-8">
                <div className="text-center mb-8">
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">{plan.name}</h3>
                  <p className="text-gray-600 mb-4">{plan.description}</p>
                  <div className="flex items-baseline justify-center">
                    <span className="text-4xl font-bold text-gray-900">¥{plan.price}</span>
                    <span className="text-gray-600 ml-1">/{plan.period}</span>
                  </div>
                </div>

                <ul className="space-y-4 mb-8">
                  {plan.features.map((feature, index) => (
                    <li key={index} className="flex items-start">
                      <svg className="h-5 w-5 text-green-500 mr-3 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      <span className="text-gray-700">{feature}</span>
                    </li>
                  ))}
                </ul>

                <button
                  onClick={() => {
                    if (!isAuthenticated) {
                      alert('请先登录');
                      return;
                    }
                    if (plan.id === 'free') {
                      alert('免费版无需支付');
                      return;
                    }
                    setSelectedPlan({ id: plan.id, name: plan.name, price: plan.price });
                    setShowPaymentModal(true);
                  }}
                  className={`w-full block text-center py-3 px-4 rounded-lg font-medium transition-colors ${
                    plan.popular
                      ? 'bg-purple-600 text-white hover:bg-purple-700'
                      : plan.id === 'free'
                      ? 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      : 'bg-orange-500 text-white hover:bg-orange-600'
                  }`}
                >
                  {plan.cta}
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* FAQ Section */}
      <div className="bg-white py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">常见问题</h2>
            <p className="text-gray-600">关于定价和订阅的常见问题解答</p>
          </div>

          <div className="space-y-8">
            <div className="border-b border-gray-200 pb-8">
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                可以随时取消订阅吗？
              </h3>
              <p className="text-gray-600">
                当然可以！您可以随时在账户设置中取消订阅。取消后您仍可使用到当前计费周期结束。
              </p>
            </div>

            <div className="border-b border-gray-200 pb-8">
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                支持哪些支付方式？
              </h3>
              <p className="text-gray-600">
                我们支持微信支付、支付宝和银行卡支付。所有支付都经过加密处理，确保安全。
              </p>
            </div>

            <div className="border-b border-gray-200 pb-8">
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                企业版有什么额外功能？
              </h3>
              <p className="text-gray-600">
                企业版提供团队协作、API访问、定制化模板、数据分析报告等高级功能，还有专属客户经理服务。
              </p>
            </div>

            <div className="pb-8">
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                有免费试用吗？
              </h3>
              <p className="text-gray-600">
                免费版可以永久使用，包含基础功能。专业版和企业版提供7天免费试用。
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="bg-gradient-to-r from-purple-600 to-purple-800 text-white py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-4">还不确定？</h2>
          <p className="text-xl text-purple-100 mb-8">
            免费版永久使用，无任何风险
          </p>
          <Link
            to="/register"
            className="inline-flex items-center px-8 py-3 bg-white text-purple-600 font-semibold rounded-lg hover:bg-gray-100 transition-colors"
          >
            立即免费试用
          </Link>
        </div>
      </div>

      {/* Payment Modal */}
      {selectedPlan && (
        <PaymentModal
          isOpen={showPaymentModal}
          onClose={() => setShowPaymentModal(false)}
          planId={selectedPlan.id}
          planName={selectedPlan.name}
          price={selectedPlan.price}
        />
      )}
    </div>
  );
};

export default PricingPage;